import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { AdvancedModelSettingsService } from './advanced-model-settings.service';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';
import { TranslationService } from './translation.service';
import { WordUsageService } from './word-usage.service';

// Declare Capacitor types for TypeScript
declare global {
  interface Window {
    Capacitor?: {
      isNativePlatform(): boolean;
    };
  }
}

@Injectable({ providedIn: 'root' })
export class TranslatorService {
  private base: string;
  private translationCache = new Map<string, string>();
  private readonly CACHE_SIZE_LIMIT = 1000; // Limit cache size

  // ⚡ PERFORMANCE: Health check optimization
  private lastRequestFailed = false;
  private lastHealthCheck = 0;
  private readonly HEALTH_CHECK_INTERVAL = 60000; // 1 minute

  // Track primed languages to avoid redundant priming
  private primedLanguages = new Set<string>();
  private isPriming = false; // Prevent concurrent priming

  private translationService = inject(TranslationService);

  constructor(
    private authService: AuthService,
    private advancedModelSettings: AdvancedModelSettingsService,
    private wordUsageService: WordUsageService,
    private toastService: ToastService,
    private router: Router
  ) {
    this.base = this.getBaseUrl();
  }

  getCurrentBaseUrl(): string {
    // Always re-detect the environment
    return this.getBaseUrl();
  }

  private getBaseUrl(): string {
    // Use environment configuration
    const apiUrl = environment.apiUrl;
    console.log('🌐 Using API URL from environment:', apiUrl);
    return apiUrl;
  }

  // ⚡ PERFORMANCE: Conditional logging (only in development)
  private log(...args: any[]) {
    if (!environment.production) {
      console.log(...args);
    }
  }

  // ⚡ PERFORMANCE: Fast English detection (replaces 400+ word regex)
  private isLikelyEnglish(text: string): boolean {
    if (text.length < 3) return false;

    // Check for non-Latin scripts (definitely not English)
    if (/[а-яА-ЯёЁ\u4e00-\u9fff가-힣]/.test(text)) return false;

    // Check for most common English words (top 20 covers 33% of all English text)
    return /\b(the|be|to|of|and|a|in|that|have|i|it|for|not|on|with|he|as|you|do|at|this)\b/i.test(
      text
    );
  }

  // ⚡ PERFORMANCE: Synchronous word limit check (no async overhead)
  // Check if user has exceeded word limit before making API calls
  private checkWordLimit(text?: string): {
    canProceed: boolean;
    message?: string;
  } {
    try {
      console.log('🔍 Checking word limit before API call...');

      // Get current usage stats (synchronous - reads from cached BehaviorSubject)
      const currentStats = this.wordUsageService.getUsageStats();
      console.log('📊 Current usage stats: [stats loaded]');
      console.log(
        '🔍 Debug - wordsUsed:',
        currentStats.wordsUsed,
        'wordsLimit:',
        currentStats.wordsLimit,
        'isOverLimit:',
        currentStats.isOverLimit
      );

      // Check if over limit
      if (currentStats.isOverLimit) {
        const wordsUsed = currentStats.wordsUsed || 0;
        const wordsLimit = currentStats.wordsLimit || 0;
        const message = this.translationService.formatMessage(
          'wordLimitExceededDetail',
          wordsUsed.toLocaleString(),
          wordsLimit.toLocaleString()
        );
        console.log('❌ Word limit exceeded:', message);

        // Don't show toast here - will be shown by error handler
        // This prevents blocking on mobile
        return { canProceed: false, message };
      }

      // Check if approaching limit (90% threshold)
      const wordsUsed = currentStats.wordsUsed || 0;
      const wordsLimit = currentStats.wordsLimit || 1; // Avoid division by zero
      const usagePercentage = (wordsUsed / wordsLimit) * 100;
      if (usagePercentage >= 90) {
        const remainingWords = wordsLimit - wordsUsed;
        const warningMessage = `Warning: You have only ${remainingWords.toLocaleString()} words remaining (${usagePercentage.toFixed(
          1
        )}% used). Consider buying more words soon.`;
        console.log('⚠️ Approaching word limit:', warningMessage);

        // Note: Warning toast disabled on mobile to prevent deadlock
        // The error toast when limit is exceeded is still shown
        // Users will see the word count in the word usage modal
      }

      console.log('✅ Word limit check passed');
      return { canProceed: true };
    } catch (error) {
      console.error('❌ Error checking word limit:', error);
      // If there's an error checking limits, allow the request to proceed
      // This prevents blocking legitimate requests due to service issues
      return { canProceed: true };
    }
  }

  // Helper method to handle 429 word limit errors consistently
  private handleWordLimitError(errorText: string): never {
    console.log('🚫 Word limit exceeded (429) - parsing error message');

    // Always use TranslationService to get message in current UI language
    let wordsUsed = 0;
    let wordsLimit = 0;

    try {
      const errorData = JSON.parse(errorText);

      // Extract usage stats from backend response
      if (errorData.usage) {
        wordsUsed = errorData.usage.wordsUsed || 0;
        wordsLimit = errorData.usage.wordsLimit || 0;
        console.log(
          '🔄 Updating word usage stats from 429 response:',
          errorData.usage
        );
        this.wordUsageService.refreshData();
      }
    } catch (parseError) {
      console.error('❌ Failed to parse word limit error:', parseError);
      // Fallback: try to get stats from service
      const stats = this.wordUsageService.getUsageStats();
      wordsUsed = stats.wordsUsed || 0;
      wordsLimit = stats.wordsLimit || 0;
    }

    // Always format message using TranslationService with current UI language
    // This ensures the message is in the user's selected language setting
    // Get UI language from localStorage (source of truth for UI language setting)
    let uiLanguage = 'en'; // Default to English
    try {
      const savedUILanguage = localStorage.getItem('ui_language');
      if (
        savedUILanguage &&
        [
          'en',
          'ko',
          'zh',
          'zh-CN',
          'zh-HK',
          'ky',
          'ru',
          'kk',
          'tg',
          'tk',
          'uz',
          'fa',
          'es',
        ].includes(savedUILanguage)
      ) {
        uiLanguage = savedUILanguage;
        // Normalize Chinese variants
        if (uiLanguage === 'zh-CN' || uiLanguage === 'zh-HK') {
          uiLanguage = 'zh';
        }
        // Ensure TranslationService is using the correct language
        this.translationService.setLanguage(uiLanguage);
      } else {
        // No preference found, ensure it's set to English
        this.translationService.setLanguage('en');
      }
    } catch (error) {
      console.error('❌ Error reading UI language from localStorage:', error);
      // Fallback to English
      this.translationService.setLanguage('en');
    }

    const errorMessage = this.translationService.formatMessage(
      'wordLimitExceededDetail',
      wordsUsed.toLocaleString(),
      wordsLimit.toLocaleString()
    );

    console.log(
      '🚫 Throwing word limit error immediately (toast will be shown by error handler)'
    );

    // Throw error immediately - the error handler in app.component.ts will show the toast
    // This prevents blocking on mobile
    throw new Error(errorMessage);
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    try {
      console.log('🔐 Getting auth token for API request...');
      const token = await this.authService.getAuthToken();
      console.log('🔍 Token retrieved: YES');

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log('✅ Authorization header set');
      } else {
        console.log('❌ No token available - request will be unauthenticated');
      }
    } catch (error) {
      console.error('❌ Error getting auth token:', error);
      // If there's an error getting the token, it might be expired/invalid
      this.handleAuthError(
        error instanceof Error ? error : new Error(String(error))
      );
    }

    console.log('📤 Final headers: [headers set]');
    return headers;
  }
  private isPlayingAudio = false;

  /**
   * Redirect user to login page when token is expired/invalid
   */
  private redirectToLogin(): void {
    console.log('🚪 Redirecting to login page due to expired/invalid token');

    // Logout the user first to clear any invalid tokens
    this.authService.logout();

    // Show a message to the user
    if (typeof window !== 'undefined' && window.alert) {
      window.alert(
        'Your session has expired. Please log in again to continue.'
      );
    }

    // Redirect to login page (assuming the app has routing set up)
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }

  async primeLanguage(
    language:
      | 'ky'
      | 'en'
      | 'ko'
      | 'zh-CN'
      | 'zh-HK'
      | 'ru'
      | 'kk'
      | 'tg'
      | 'tk'
      | 'uz'
      | 'fa'
  ) {
    // Skip if already primed
    if (this.primedLanguages.has(language)) {
      console.log(`⏭️ Language ${language} already primed, skipping`);
      return { success: true, cached: true };
    }

    try {
      const headers = await this.getAuthHeaders();
      const r = await fetch(`${this.base}/api/prime-language`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ language }),
      });
      if (!r.ok) throw new Error(await r.text());
      const result = await r.json();
      // Mark as primed on success
      if (result.success !== false) {
        this.primedLanguages.add(language);
        console.log(`✅ Language ${language} primed successfully`);
      }
      return result;
    } catch (error) {
      console.warn('Language priming failed:', error);
      // Don't throw error, just log it as priming is optional
      return { success: false };
    }
  }

  /**
   * Ensure languages are primed before translation
   * This fixes the issue where first translation doesn't work
   */
  private async ensureLanguagesPrimed(
    target: string,
    sourceLanguage?: string
  ): Promise<void> {
    // Skip if already priming to avoid concurrent calls
    if (this.isPriming) {
      return;
    }

    // Check if we need to prime
    const needsPriming: string[] = [];
    if (target && !this.primedLanguages.has(target)) {
      needsPriming.push(target);
    }
    if (sourceLanguage && !this.primedLanguages.has(sourceLanguage)) {
      needsPriming.push(sourceLanguage);
    }

    if (needsPriming.length === 0) {
      return; // Already primed
    }

    // Map 'zh' to 'zh-CN' for priming (backend expects specific variant)
    const primableLanguages = needsPriming.map((lang) => {
      if (lang === 'zh') return 'zh-CN';
      return lang;
    }).filter((lang): lang is 'ky' | 'en' | 'ko' | 'zh-CN' | 'zh-HK' | 'ru' | 'kk' | 'tg' | 'tk' | 'uz' | 'fa' => {
      // Only prime languages that are supported by primeLanguage API
      return ['ky', 'en', 'ko', 'zh-CN', 'zh-HK', 'ru', 'kk', 'tg', 'tk', 'uz', 'fa'].includes(lang);
    });

    if (primableLanguages.length === 0) {
      return; // No primable languages
    }

    this.isPriming = true;
    try {
      console.log('🔥 Priming languages before first translation:', primableLanguages);
      // Prime all needed languages in parallel
      await Promise.allSettled(
        primableLanguages.map((lang) => this.primeLanguage(lang))
      );
    } finally {
      this.isPriming = false;
    }
  }

  async transcribe(
    blob: Blob,
    translateToEnglish: boolean,
    language?: string,
    systemPrompt?: string,
    useDiarization?: boolean,
    targetLanguage?: string,
    sourceLanguage?: string,
    conversationContext?: string[],
    useAdvancedModel = false
  ) {
    console.log('🎤 Starting transcription...');
    console.log('📊 Blob size: [audio data]');
    const currentBase = this.getCurrentBaseUrl();
    console.log('🌐 Base URL:', currentBase);
    console.log('🔄 Translate to English:', translateToEnglish);
    console.log('🗣️ Language:', language);
    console.log('👥 Use Diarization:', useDiarization);
    const effectiveUseAdvancedModel =
      this.advancedModelSettings.getEffectiveUseAdvancedModel(useAdvancedModel);

    // Prime languages before first transcription if not already primed
    // This ensures backend is ready with language context
    const transcriptionLang = sourceLanguage || language;
    const translationTarget = targetLanguage || (translateToEnglish ? 'en' : undefined);
    if (transcriptionLang || translationTarget) {
      await this.ensureLanguagesPrimed(translationTarget || '', transcriptionLang);
    }

    // Word limit check moved to backend - backend will return 429 if limit exceeded

    // Test basic connectivity first
    console.log('🧪 Testing basic connectivity...');
    try {
      const testUrl = `${currentBase}/health`;
      console.log('🔍 Testing health endpoint:', testUrl);
      const testResponse = await fetch(testUrl, { method: 'GET' });
      console.log('🏥 Health check response:', {
        status: testResponse.status,
        ok: testResponse.ok,
        statusText: testResponse.statusText,
      });
      if (testResponse.ok) {
        const healthData = await testResponse.json();
        console.log('✅ Health check data:', healthData);
      } else {
        console.error(
          '❌ Health check failed with status:',
          testResponse.status
        );
      }
    } catch (healthError) {
      console.error('❌ Health check failed:', healthError);
    }

    const fd = new FormData();

    // Determine the correct file extension based on blob type
    let filename = 'audio.webm'; // default
    if (blob.type.includes('mp4')) filename = 'audio.mp4';
    else if (blob.type.includes('wav')) filename = 'audio.wav';
    else if (blob.type.includes('m4a')) filename = 'audio.m4a';
    else if (blob.type.includes('mp3')) filename = 'audio.mp3';
    else if (blob.type.includes('ogg')) filename = 'audio.ogg';
    else if (blob.type.includes('webm')) filename = 'audio.webm';
    else if (blob.type.includes('flac')) filename = 'audio.flac';
    else if (blob.type.includes('mpeg')) filename = 'audio.mpeg';
    else if (blob.type.includes('mpga')) filename = 'audio.mpga';
    else if (blob.type.includes('oga')) filename = 'audio.oga';

    console.log('📁 Using filename:', filename);
    console.log('📄 Blob type:', blob.type);

    fd.append('audio', blob, filename);
    fd.append('translateToEnglish', String(translateToEnglish));
    if (language) {
      fd.append('language', language);
    }
    if (sourceLanguage) {
      fd.append('sourceLanguage', sourceLanguage);
      console.log('🔤 Source language (new param):', sourceLanguage);
    }
    if (targetLanguage) {
      fd.append('targetLanguage', targetLanguage);
      console.log('🎯 Target language:', targetLanguage);
    }
    if (systemPrompt) {
      fd.append('systemPrompt', systemPrompt);
      console.log(
        '🔍 System prompt contains WRONG_LANGUAGE_DETECTED:',
        systemPrompt.includes('WRONG_LANGUAGE_DETECTED')
      );
      console.log('📝 System prompt provided (conversation topic/context)');
    } else {
      console.log(
        'ℹ️ No system prompt provided (optional - conversation topic not set)'
      );
    }
    if (useDiarization !== undefined) {
      fd.append('useDiarization', String(useDiarization));
      console.log('👥 Diarization parameter added:', useDiarization);
    }
    fd.append('useAdvancedModel', String(effectiveUseAdvancedModel));
    console.log('🧠 Advanced translation model:', effectiveUseAdvancedModel);
    if (
      conversationContext &&
      Array.isArray(conversationContext) &&
      conversationContext.length > 0
    ) {
      fd.append('conversationContext', JSON.stringify(conversationContext));
      console.log('📚 Conversation context added:', {
        chunkCount: conversationContext.length,
        preview: conversationContext.map((t) => t.substring(0, 30)),
      });
    }

    const url = `${currentBase}/api/transcribe`;
    console.log('🚀 Making request to:', url);

    try {
      const headers = await this.getAuthHeaders();
      // Remove Content-Type from headers for FormData (browser will set it with boundary)
      delete headers['Content-Type'];

      const r = await fetch(url, {
        method: 'POST',
        headers,
        body: fd,
      });

      console.log('📡 Response status:', r.status);
      console.log('📡 Response ok:', r.ok);

      if (!r.ok) {
        const errorText = await r.text();
        console.error('❌ API Error:', errorText);

        // Check if it's a 401/403 authentication error (token expired/invalid)
        if (r.status === 401 || r.status === 403) {
          console.log(
            `🚪 ${r.status} ${
              r.status === 401 ? 'Unauthorized' : 'Forbidden'
            } - token expired or invalid`
          );
          this.handleAuthError(
            new Error(
              `${r.status} ${
                r.status === 401 ? 'Unauthorized' : 'Forbidden'
              }: ${errorText}`
            )
          );
          throw new Error('Authentication failed. Please log in again.');
        }

        // Check if it's a 400 error with "No speech detected" - this is expected when user stops without speaking
        if (r.status === 400) {
          try {
            const errorJson = JSON.parse(errorText);
            if (
              errorJson.error &&
              (errorJson.error.includes('No speech detected') ||
                errorJson.error.includes('silence'))
            ) {
              console.log(
                'ℹ️ No speech detected (silence) - this is expected when stopping without speaking, handling silently'
              );
              // Return empty response instead of throwing error
              return {
                text: '',
                confidence: 0,
                originalText: '',
                translatedText: '',
                useDiarization: false,
              };
            }
          } catch (parseError) {
            // If errorText is not JSON, continue with normal error handling
          }
        }

        // Check if it's a 429 error - could be word limit or OpenAI quota
        if (r.status === 429) {
          // Check if it's an OpenAI quota error (different from word limit)
          if (
            errorText.includes('quota') ||
            errorText.includes('insufficient_quota') ||
            errorText.includes('exceeded your current quota')
          ) {
            console.error('❌ OpenAI API quota exceeded:', errorText);
            const quotaError =
              'OpenAI API quota exceeded. Please add a payment method or buy credits at https://platform.openai.com/account/billing';
            this.toastService.showError(quotaError, 10000);
            throw new Error(quotaError);
          } else {
            // This is a word limit error from our backend
            this.handleWordLimitError(errorText);
          }
        }

        throw new Error(errorText);
      }

      // Parse response with detailed logging
      let result;
      try {
        const responseText = await r.text();
        console.log('📄 Raw response text length:', responseText.length);
        console.log(
          '📄 Raw response text (first 500 chars):',
          responseText.substring(0, 500)
        );

        result = JSON.parse(responseText);
        console.log('✅ Transcription result parsed:', result);
        console.log('✅ Result keys:', Object.keys(result));
        console.log('✅ Result text:', result.text);
        console.log('✅ Result originalText:', result.originalText);
        console.log('✅ Result translatedText:', result.translatedText);
        console.log('✅ Result error:', result.error);
        console.log('✅ Result confidence:', result.confidence);

        // Check for OpenAI quota error in response body
        if (result.error) {
          if (
            result.error.includes('quota') ||
            result.error.includes('insufficient_quota') ||
            result.error.includes('exceeded your current quota')
          ) {
            console.error(
              '❌ OpenAI API quota exceeded in response:',
              result.error
            );
            const quotaError =
              'OpenAI API quota exceeded. Please add a payment method or buy credits at https://platform.openai.com/account/billing';
            this.toastService.showError(quotaError, 10000);
            throw new Error(quotaError);
          }
        }
      } catch (parseError) {
        console.error('❌ Failed to parse transcription response:', parseError);
        throw new Error('Failed to parse transcription response from server');
      }

      console.log(
        '🔍 Result text contains WRONG_LANGUAGE_DETECTED:',
        result.text?.includes('WRONG_LANGUAGE_DETECTED')
      );
      if (result.useDiarization) {
        console.log('👥 Diarization segments:', result.segments);
      }

      // Track transcription activity if successful (backend will calculate word counts)
      if (result.text && !result.text.includes('WRONG_LANGUAGE_DETECTED')) {
        const audioMinutes = blob ? (blob.size / (1024 * 1024)) * 0.1 : 0; // Rough estimate

        // Track transcription activity (backend will calculate word counts from raw text)
        this.wordUsageService
          .trackTranscription(result.text, audioMinutes)
          .subscribe({
            next: (success) => {
              console.log('🎤 Transcription activity tracked successfully');
              // Refresh word usage data to update the display with a small delay
              setTimeout(() => {
                console.log(
                  '🔄 Refreshing word usage data after transcription...'
                );
                this.wordUsageService.refreshData();
              }, 500);
            },
            error: (error) => {
              console.error(
                '❌ Failed to track transcription activity:',
                error
              );
            },
          });
      }

      return {
        text: result.text as string,
        confidence: result.confidence as number,
        wrongLanguage: result.wrongLanguage as boolean,
        message: result.message as string,
        warning: result.warning as string,
        error: result.error as string,
        useDiarization: result.useDiarization as boolean,
        segments: result.segments as any[],
        originalText: result.originalText as string | undefined, // Original text in source language (when translation was performed)
        translatedText: result.translatedText as string | undefined, // Translated text in target language (when translation was performed)
      };
    } catch (error) {
      console.error('💥 Transcription error in TranslatorService:', error);
      if (error instanceof Error) {
        console.error('💥 Error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name,
        });
      } else {
        console.error('💥 Non-Error object:', JSON.stringify(error, null, 2));
      }
      throw error;
    }
  }

  // Bidirectional transcription: auto-detects language and translates accordingly
  // If source language detected → translates to target
  // If target language detected → translates to source
  async bidirectionalTranscribe(
    blob: Blob,
    sourceLanguage: string,
    targetLanguage: string,
    systemPrompt?: string,
    conversationContext?: string[],
    useAdvancedModel = false
  ): Promise<{
    detectedLanguage: string;
    originalText: string;
    translatedText: string;
    translationDirection: 'source-to-target' | 'target-to-source' | 'none';
    confidence?: number;
  }> {
    console.log('🔄 Starting bidirectional transcription...');
    console.log('📊 Blob size: [audio data]');
    const currentBase = this.getCurrentBaseUrl();
    console.log('🌐 Base URL:', currentBase);
    console.log('🔤 Source language:', sourceLanguage);
    console.log('🎯 Target language:', targetLanguage);
    const effectiveUseAdvancedModel =
      this.advancedModelSettings.getEffectiveUseAdvancedModel(useAdvancedModel);

    const fd = new FormData();

    // Determine the correct file extension based on blob type
    let filename = 'audio.webm'; // default
    if (blob.type.includes('mp4')) filename = 'audio.mp4';
    else if (blob.type.includes('wav')) filename = 'audio.wav';
    else if (blob.type.includes('m4a')) filename = 'audio.m4a';
    else if (blob.type.includes('mp3')) filename = 'audio.mp3';
    else if (blob.type.includes('ogg')) filename = 'audio.ogg';
    else if (blob.type.includes('webm')) filename = 'audio.webm';
    else if (blob.type.includes('flac')) filename = 'audio.flac';

    fd.append('audio', blob, filename);
    fd.append('sourceLanguage', sourceLanguage);
    fd.append('targetLanguage', targetLanguage);
    fd.append('useAdvancedModel', String(effectiveUseAdvancedModel));

    if (systemPrompt) {
      fd.append('systemPrompt', systemPrompt);
      console.log('📝 System prompt provided');
    }

    if (
      conversationContext &&
      Array.isArray(conversationContext) &&
      conversationContext.length > 0
    ) {
      fd.append('conversationContext', JSON.stringify(conversationContext));
      console.log('📚 Conversation context provided:', {
        chunkCount: conversationContext.length,
        preview: conversationContext.map((t) => t.substring(0, 30)),
      });
    }

    const url = `${currentBase}/api/bidirectional-transcribe`;
    console.log('🚀 Making request to:', url);

    try {
      const headers = await this.getAuthHeaders();
      // Remove Content-Type from headers for FormData
      delete headers['Content-Type'];

      const r = await fetch(url, {
        method: 'POST',
        headers,
        body: fd,
      });

      console.log('📡 Response status:', r.status);
      console.log('📡 Response ok:', r.ok);

      if (!r.ok) {
        const errorText = await r.text();
        console.error('❌ API Error:', errorText);

        // Check if it's a 401/403 authentication error
        if (r.status === 401 || r.status === 403) {
          this.handleAuthError(
            new Error(
              `${r.status} ${
                r.status === 401 ? 'Unauthorized' : 'Forbidden'
              }: ${errorText}`
            )
          );
          throw new Error('Authentication failed. Please log in again.');
        }

        // Check if it's a 429 error - word limit
        if (r.status === 429) {
          this.handleWordLimitError(errorText);
        }

        throw new Error(errorText);
      }

      const result = await r.json();
      console.log('✅ Bidirectional transcription result:', {
        detectedLanguage: result.detectedLanguage,
        translationDirection: result.translationDirection,
        originalText: result.originalText?.substring(0, 50),
        translatedText: result.translatedText?.substring(0, 50),
      });

      return {
        detectedLanguage: result.detectedLanguage || 'unknown',
        originalText: result.originalText || '',
        translatedText: result.translatedText || '',
        translationDirection: result.translationDirection || ('none' as const),
        confidence: result.confidence,
      };
    } catch (error) {
      console.error('💥 Bidirectional transcription error:', error);
      if (error instanceof Error) {
        console.error('💥 Error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name,
        });
      }
      throw error;
    }
  }

  // New method for diarization with translation
  async transcribeWithDiarization(
    blob: Blob,
    language: string,
    translateToEnglish: boolean = false,
    targetLanguage?: string,
    sourceLanguage?: string
  ) {
    console.log('👥 Starting diarization transcription...');

    // Determine source and target languages
    const sourceLang = sourceLanguage || language;
    const targetLang =
      targetLanguage || (translateToEnglish ? 'en' : undefined);

    // translateToEnglish should be true only if target language is English
    const translateToEng = translateToEnglish || targetLang === 'en';

    // First transcribe with diarization
    const transcriptionResult = await this.transcribe(
      blob,
      translateToEng,
      language,
      undefined, // systemPrompt
      true, // useDiarization
      targetLang, // targetLanguage
      sourceLang // sourceLanguage
    );

    if (!transcriptionResult.useDiarization || !transcriptionResult.segments) {
      console.warn(
        '⚠️ Diarization not available, falling back to regular transcription'
      );
      return {
        text: transcriptionResult.text,
        confidence: transcriptionResult.confidence,
        segments: [],
        translatedSegments: [],
      };
    }

    // Translate each segment if needed
    const translatedSegments = [];
    if (translateToEnglish) {
      console.log('🔄 Translating diarization segments...');
      for (const segment of transcriptionResult.segments) {
        try {
          const translation = await this.translate(
            segment.text,
            'en',
            language
          );
          translatedSegments.push({
            ...segment,
            translatedText: translation,
          });
        } catch (error) {
          console.error('❌ Error translating segment:', error);
          translatedSegments.push({
            ...segment,
            translatedText: segment.text, // Fallback to original text
          });
        }
      }
    } else {
      // No translation needed, just copy segments
      translatedSegments.push(...transcriptionResult.segments);
    }

    return {
      text: transcriptionResult.text,
      confidence: transcriptionResult.confidence,
      segments: transcriptionResult.segments,
      translatedSegments: translatedSegments,
    };
  }

  /**
   * Broadcast transcription: Send audio to backend for transcription and multi-language translation
   * Backend handles: 1 transcription + N translations + saving all to Firestore
   */
  async broadcastTranscribe(
    audioBlob: Blob,
    broadcastCode: string,
    sourceLanguage: string,
    targetLanguages: string[],
    userId: string,
    userEmail: string,
    conversationTopic?: string,
    conversationContext?: string[],
    useAdvancedModel = false
  ): Promise<{
    success: boolean;
    skipped?: boolean;
    sourceText: string;
    translations: Array<{ targetLanguage: string; translatedText: string }>;
  }> {
    console.log(
      '📡 [Broadcast] Sending audio to backend for transcription and translation'
    );
    console.log('📊 [Broadcast] Audio blob size:', audioBlob.size);
    console.log('🎯 [Broadcast] Target languages:', targetLanguages);
    const effectiveUseAdvancedModel =
      this.advancedModelSettings.getEffectiveUseAdvancedModel(useAdvancedModel);
    if (conversationTopic) {
      console.log('📝 [Broadcast] Conversation topic:', conversationTopic);
    }

    const currentBase = this.getCurrentBaseUrl();
    const fd = new FormData();

    // Determine the correct file extension based on blob type
    let filename = 'audio.webm'; // default
    if (audioBlob.type.includes('mp4')) filename = 'audio.mp4';
    else if (audioBlob.type.includes('wav')) filename = 'audio.wav';
    else if (audioBlob.type.includes('m4a')) filename = 'audio.m4a';
    else if (audioBlob.type.includes('mp3')) filename = 'audio.mp3';
    else if (audioBlob.type.includes('ogg')) filename = 'audio.ogg';
    else if (audioBlob.type.includes('webm')) filename = 'audio.webm';
    else if (audioBlob.type.includes('flac')) filename = 'audio.flac';

    fd.append('audio', audioBlob, filename);
    fd.append('broadcastCode', broadcastCode);
    fd.append('sourceLanguage', sourceLanguage);
    fd.append('targetLanguages', JSON.stringify(targetLanguages));
    fd.append('useAdvancedModel', String(effectiveUseAdvancedModel));
    if (conversationTopic) {
      fd.append('conversationTopic', conversationTopic);
    }
    if (conversationContext && conversationContext.length > 0) {
      fd.append('conversationContext', JSON.stringify(conversationContext));
      console.log(
        '📚 [Broadcast] Conversation context provided:',
        conversationContext.map((c) => c.substring(0, 30))
      );
    }

    const url = `${currentBase}/api/broadcast/transcribe`;
    console.log('🚀 [Broadcast] Making request to:', url);

    try {
      const headers = await this.getAuthHeaders();
      // Remove Content-Type from headers for FormData (browser will set it with boundary)
      delete headers['Content-Type'];

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: fd,
      });

      console.log('📡 [Broadcast] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [Broadcast] API Error:', errorText);

        if (response.status === 401 || response.status === 403) {
          this.handleAuthError(
            new Error(`Authentication failed: ${errorText}`)
          );
          throw new Error('Authentication failed. Please log in again.');
        }

        if (response.status === 429) {
          this.handleWordLimitError(errorText);
        }

        throw new Error(errorText);
      }

      const result = await response.json();
      console.log('✅ [Broadcast] Success:', {
        sourceText: result.sourceText?.substring(0, 50),
        translationsCount: result.translations?.length,
        languages: result.translations?.map((t: any) => t.targetLanguage),
      });

      return result;
    } catch (error) {
      console.error('❌ [Broadcast] Transcription error:', error);
      throw error;
    }
  }

  /** Short-lived OpenAI client secret for a gpt-realtime-translate WebRTC call. */
  async createLiveTranslationSession(
    sourceLanguage: string,
    targetLanguage: string
  ): Promise<{ clientSecret: string; expiresAt: number }> {
    return this.postJson('/api/live-translation/session', {
      sourceLanguage,
      targetLanguage,
    });
  }

  async recordLiveTranslationUsage(
    sourceText: string,
    translatedText: string,
    sourceLanguage: string,
    targetLanguage: string
  ): Promise<void> {
    await this.postJson('/api/live-translation/usage', {
      sourceText,
      translatedText,
      sourceLanguage,
      targetLanguage,
    });
    this.wordUsageService.refreshData();
  }

  /**
   * Live broadcast: translate the English pivot text of one spoken sentence
   * into every target language and save it for listeners.
   */
  async broadcastTranslateText(
    broadcastCode: string,
    sourceLanguage: string,
    sourceText: string,
    englishText: string,
    targetLanguages: string[],
    conversationTopic?: string,
    conversationContext?: string[]
  ): Promise<{
    success: boolean;
    skipped?: boolean;
    sourceText: string;
    translations: Array<{ targetLanguage: string; translatedText: string }>;
  }> {
    return this.postJson('/api/broadcast/translate-text', {
      broadcastCode,
      sourceLanguage,
      sourceText,
      englishText,
      targetLanguages,
      conversationTopic,
      conversationContext,
    });
  }

  private async postJson<T>(path: string, body: unknown): Promise<T> {
    const response = await fetch(`${this.getCurrentBaseUrl()}${path}`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorText = await response.text();
      if (response.status === 401 || response.status === 403) {
        this.handleAuthError(new Error(`Authentication failed: ${errorText}`));
        throw new Error('Authentication failed. Please log in again.');
      }
      if (response.status === 429) {
        try {
          this.handleWordLimitError(errorText);
        } catch (wordLimitError: any) {
          wordLimitError.isWordLimit = true;
          throw wordLimitError;
        }
      }
      let message = errorText;
      try {
        const parsed = JSON.parse(errorText);
        message = parsed.message || parsed.error || errorText;
      } catch {
        // Non-JSON error body
      }
      throw new Error(message);
    }

    return response.json();
  }

  async translate(
    text: string,
    target:
      | 'ky'
      | 'en'
      | 'ko'
      | 'zh-CN'
      | 'zh-HK'
      | 'ru'
      | 'kk'
      | 'tg'
      | 'tk'
      | 'uz'
      | 'fa'
      | 'es',
    sourceLanguage?: string,
    userLanguage?: string,
    partnerLanguage?: string,
    conversationTopic?: string,
    useAdvancedModel = false
  ) {
    console.log('🔄 Starting translation...');
    console.log('📝 Text to translate:', text);
    console.log('🎯 Target language:', target);
    console.log('🔤 Source language:', sourceLanguage);
    const effectiveUseAdvancedModel =
      this.advancedModelSettings.getEffectiveUseAdvancedModel(useAdvancedModel);

    // Prime languages before first translation if not already primed
    // This ensures backend is ready with language context
    await this.ensureLanguagesPrimed(target, sourceLanguage);

    // Word limit check moved to backend - backend will return 429 if limit exceeded

    // Removed client-side language detection - let LLM handle mixed language input gracefully

    // Check cache first for performance
    const cacheKey = `${text}|${target}|${sourceLanguage}|advanced:${effectiveUseAdvancedModel}`;
    if (this.translationCache.has(cacheKey)) {
      console.log('⚡ Using cached translation');
      return this.translationCache.get(cacheKey)!;
    }

    // Get current base URL dynamically
    const currentBase = this.getCurrentBaseUrl();

    // ⚡ PERFORMANCE: Only check health if last request failed or it's been >1 minute
    if (
      this.lastRequestFailed ||
      Date.now() - this.lastHealthCheck > this.HEALTH_CHECK_INTERVAL
    ) {
      try {
        const testResponse = await fetch(`${currentBase}/health`, {
          method: 'GET',
        });
        if (testResponse.ok) {
          this.lastRequestFailed = false;
          this.lastHealthCheck = Date.now();
        }
      } catch (healthError) {
        // Health check failed, but continue anyway - let the actual request fail if needed
        this.lastRequestFailed = true;
      }
    }
    // ⚡ PERFORMANCE: Fast English detection using heuristics (5-15ms faster)
    // Filter out English speech when translating to English (Kyrgyz lane)
    if (target === 'en' && this.isLikelyEnglish(text)) {
      console.log(
        'Client-side filtered out English speech in Kyrgyz lane:',
        text
      );
      return ''; // Return empty string instead of translating
    }

    // Create translation system prompt with language selection context
    const translationSystemPrompt = this.getTranslationSystemPrompt(
      target,
      sourceLanguage,
      userLanguage,
      partnerLanguage,
      conversationTopic
    );
    console.log(
      '📝 Translation system prompt length:',
      translationSystemPrompt.length
    );

    const url = `${currentBase}/api/translate`;
    console.log('🚀 Making translation request to:', url);

    const requestBody: any = {
      text,
      target,
      systemPrompt: translationSystemPrompt,
      useAdvancedModel: effectiveUseAdvancedModel,
    };

    // Include sourceLanguage if provided - backend needs it for accurate translation
    if (sourceLanguage) {
      requestBody.sourceLanguage = sourceLanguage;
    }

    console.log('📤 Request summary:', {
      textLength: text.length,
      target: target,
      sourceLanguage: sourceLanguage,
      systemPromptLength: translationSystemPrompt.length,
    });

    try {
      const headers = await this.getAuthHeaders();
      const r = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      });

      console.log('📡 Translation response status:', r.status);
      console.log('📡 Translation response ok:', r.ok);

      if (!r.ok) {
        const errorText = await r.text();
        console.error('❌ Translation API Error:', errorText);

        // Check if it's a 401/403 authentication error (token expired/invalid)
        if (r.status === 401 || r.status === 403) {
          console.log(
            `🚪 ${r.status} ${
              r.status === 401 ? 'Unauthorized' : 'Forbidden'
            } - token expired or invalid`
          );
          this.handleAuthError(
            new Error(
              `${r.status} ${
                r.status === 401 ? 'Unauthorized' : 'Forbidden'
              }: ${errorText}`
            )
          );
          throw new Error('Authentication failed. Please log in again.');
        }

        // Check if it's a 429 error - could be word limit or OpenAI quota
        if (r.status === 429) {
          // Check if it's an OpenAI quota error (different from word limit)
          if (
            errorText.includes('quota') ||
            errorText.includes('insufficient_quota') ||
            errorText.includes('exceeded your current quota')
          ) {
            console.error('❌ OpenAI API quota exceeded:', errorText);
            const quotaError =
              'OpenAI API quota exceeded. Please add a payment method or buy credits at https://platform.openai.com/account/billing';
            this.toastService.showError(quotaError, 10000);
            throw new Error(quotaError);
          } else {
            // This is a word limit error from our backend
            this.handleWordLimitError(errorText);
          }
        }

        throw new Error(errorText);
      }

      const result = await r.json();
      console.log('✅ Translation result:', result);
      console.log('🎯 TRANSLATION DEBUG - Received translation:', result.text);

      let translation = result.text as string;

      // Client-side validation and filtering for translation quality
      translation = this.validateAndFilterTranslation(
        translation,
        text,
        target
      );

      // Note: Word tracking removed from automatic voice translations
      // Only manual translation box and transcription are tracked

      // Cache the translation for future use
      this.cacheTranslation(cacheKey, translation);

      return translation;
    } catch (error) {
      console.error('💥 Translation fetch error:', error);
      console.error('💥 Error details:', {
        message: error instanceof Error ? error.message : String(error),
        name: error instanceof Error ? error.name : 'Unknown',
        stack: error instanceof Error ? error.stack : undefined,
        type: typeof error,
      });

      // Check if it's a network error
      if (
        error instanceof TypeError &&
        error.message.includes('Failed to fetch')
      ) {
        console.error('🌐 Network error detected - possible causes:');
        console.error('  - No internet connection');
        console.error('  - API server is down');
        console.error('  - CORS issues');
        console.error('  - Android emulator network configuration');
        console.error('  - URL:', url);
      }

      throw error;
    }
  }

  async tts(
    text: string,
    speed: number = 1.0,
    voice: string, // User's selected voice (required, not defaulted)
    targetLanguage?: string // Optional: target language code (e.g., 'ky', 'en', 'ru')
  ): Promise<{ audioUrl: string; audioData: string } | null> {
    // Filter out unwanted TTS responses on client side too
    const filteredText = text.toLowerCase().trim();
    if (
      filteredText.includes('thank you for watching') ||
      filteredText.includes('thank you for watching!') ||
      filteredText.includes('thanks for watching') ||
      filteredText.includes('thanks for watching!') ||
      filteredText === 'thank you for watching' ||
      filteredText === 'thanks for watching'
    ) {
      console.log('Client-side filtered out unwanted TTS response:', text);
      return null;
    }

    // IMPORTANT: TTS uses the TRANSLATED text (target language), not the original text
    // OpenAI TTS automatically detects the language from the text and pronounces it correctly
    // This ensures that translated text is spoken in the target language, not the source language
    if (targetLanguage) {
      console.log(
        `🎵 TTS: Using translated text for ${targetLanguage} - OpenAI will auto-detect and pronounce correctly`
      );
    }
    console.log(
      '🎵 TTS: Converting translated text to audio (not original text)'
    );

    // TTS does not consume word quota - it's just converting already-translated text to audio
    // Word quota is only consumed during the translation/transcription process

    // Add timeout for TTS requests (especially important for long audio)
    // Longer timeout for TTS since audio generation can take time for long texts
    const TTS_TIMEOUT_MS = 60000; // 60 seconds for TTS (longer than translation)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TTS_TIMEOUT_MS);

    try {
      const headers = await this.getAuthHeaders();
      const r = await fetch(`${this.base}/api/tts`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          text,
          format: 'mp3',
          voice,
          language: targetLanguage,
        }),
        signal: controller.signal, // Add timeout signal
      });

      clearTimeout(timeoutId); // Clear timeout if request completes successfully

      if (!r.ok) {
        const errorText = await r.text();
        // Don't throw error for filtered responses
        if (errorText.includes('Filtered out unwanted TTS response')) {
          console.log('Server-side filtered out unwanted TTS response:', text);
          return null;
        }

        // Check if it's a 401 Unauthorized error (token expired/invalid)
        if (r.status === 401) {
          console.log('🚪 401 Unauthorized - token expired or invalid');
          this.handleAuthError(new Error('401 Unauthorized: ' + errorText));
          throw new Error('Authentication failed. Please log in again.');
        }

        // Check if it's a 429 word limit exceeded error
        if (r.status === 429) {
          console.log(
            '🚫 Word limit exceeded (429) - showing user-friendly message'
          );
          try {
            const errorData = JSON.parse(errorText);
            if (errorData.message) {
              console.log('📢 Showing word limit message:', errorData.message);
              // Show the specific word limit message to the user
              await this.toastService.showError(errorData.message, 6000);
              throw new Error(errorData.message);
            }
          } catch (parseError) {
            console.error('❌ Failed to parse word limit error:', parseError);
          }
          // Fallback message if parsing fails
          await this.toastService.showError(
            this.translationService.getTranslation('wordLimitExceededMessage'),
            6000
          );
          throw new Error(
            'Word limit exceeded. Please buy more words to continue.'
          );
        }

        // Check if it's a 504 TTS generation timeout error
        if (r.status === 504) {
          console.log(
            '⏱️ TTS generation timeout (504) - audio may be too long'
          );
          try {
            const errorData = JSON.parse(errorText);
            if (errorData.message) {
              console.log('📢 Showing TTS timeout message:', errorData.message);
              await this.toastService.showError(errorData.message, 8000);
              throw new Error(errorData.message);
            }
          } catch (parseError) {
            console.error('❌ Failed to parse TTS timeout error:', parseError);
          }
          // Fallback message
          await this.toastService.showError(
            'TTS generation timed out. The text may be too long. Please try with shorter text.',
            8000
          );
          throw new Error(
            'TTS generation timed out. Please try with shorter text.'
          );
        }

        throw new Error(errorText);
      }

      // Real TTS mode - return both audio URL and base64 data
      let blob: Blob;
      try {
        blob = await r.blob();
      } catch (blobError: any) {
        if (blobError.name === 'AbortError') {
          throw new Error(
            'TTS request timed out. The audio may be too long. Please try with shorter text.'
          );
        }
        throw blobError;
      }
      const audioUrl = URL.createObjectURL(blob);

      // Convert to base64 immediately while blob is still valid
      const audioData = await this.blobToBase64(blob);

      // Don't auto-play here - let the app component handle audio playback
      // this.playAudio(audioUrl, speed);

      return { audioUrl, audioData };
    } catch (error: any) {
      clearTimeout(timeoutId); // Always clear timeout on error
      if (error.name === 'AbortError') {
        throw new Error(
          'TTS request timed out after 60 seconds. The audio may be too long. Please try with shorter text or split the translation.'
        );
      }
      throw error;
    }
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        resolve(base64);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }

  private currentAudio?: HTMLAudioElement;

  private playAudio(audioUrl: string, speed: number = 1.0) {
    // Stop any currently playing audio
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
    }

    this.isPlayingAudio = true;
    this.currentAudio = new Audio(audioUrl);
    this.currentAudio.volume = 0.8; // Set volume to 80%
    this.currentAudio.playbackRate = speed; // Set playback speed

    // Play the audio
    this.currentAudio.play().catch((error) => {
      console.warn('Auto-play failed:', error);
      this.isPlayingAudio = false;
      // If auto-play fails, we can still return the URL for manual play
    });

    // Clean up the URL after playing to free memory
    this.currentAudio.addEventListener('ended', () => {
      this.isPlayingAudio = false;
      this.currentAudio = undefined;
      URL.revokeObjectURL(audioUrl);
    });

    this.currentAudio.addEventListener('error', () => {
      this.isPlayingAudio = false;
      this.currentAudio = undefined;
    });
  }

  get playingAudio() {
    return this.isPlayingAudio;
  }

  // Cache management methods
  private cacheTranslation(key: string, translation: string): void {
    // Limit cache size to prevent memory issues
    if (this.translationCache.size >= this.CACHE_SIZE_LIMIT) {
      // Remove oldest entries (Map maintains insertion order)
      const firstKey = this.translationCache.keys().next().value;
      if (firstKey) {
        this.translationCache.delete(firstKey);
      }
    }

    this.translationCache.set(key, translation);
    console.log(
      `💾 Cached translation. Cache size: ${this.translationCache.size}`
    );
  }

  // Clear cache if needed
  clearTranslationCache(): void {
    this.translationCache.clear();
    console.log('🗑️ Translation cache cleared');
  }

  // Clear specific cache entry for a translation
  clearTranslationCacheEntry(
    text: string,
    target: string,
    sourceLanguage?: string
  ): void {
    const cacheKey = `${text}|${target}|${sourceLanguage}`;
    if (this.translationCache.has(cacheKey)) {
      this.translationCache.delete(cacheKey);
      console.log('🗑️ Cleared cache entry for:', cacheKey);
    }
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): { size: number; limit: number; keys: string[] } {
    return {
      size: this.translationCache.size,
      limit: this.CACHE_SIZE_LIMIT,
      keys: Array.from(this.translationCache.keys()),
    };
  }

  /**
   * Get language-specific system prompt for translation with language selection context
   */
  private getTranslationSystemPrompt(
    targetLanguage: string,
    sourceLanguage?: string,
    userLanguage?: string,
    partnerLanguage?: string,
    conversationTopic?: string
  ): string {
    const languageNames: { [key: string]: string } = {
      en: 'English',
      ko: 'Korean (한국어)',
      'zh-CN': 'Mandarin (普通话)',
      'zh-HK': 'Cantonese (粵語)',
      zh: 'Mandarin (普通话)', // Backward compatibility
      ky: 'Kyrgyz (Кыргызча)',
      ru: 'Russian (Русский)',
      kk: 'Kazakh (Қазақша)',
      tg: 'Tajik (Тоҷикӣ)',
      tk: 'Turkmen (Türkmençe)',
      uz: "Uzbek (O'zbekcha)",
      fa: 'Farsi (فارسی)',
    };

    const targetName = languageNames[targetLanguage] || targetLanguage;
    const sourceName = sourceLanguage
      ? languageNames[sourceLanguage] || sourceLanguage
      : 'the source language';

    // Build language selection context
    let languageContext = '';
    if (userLanguage && partnerLanguage) {
      const userLangName = languageNames[userLanguage] || userLanguage;
      const partnerLangName = languageNames[partnerLanguage] || partnerLanguage;
      languageContext = `

🎯 LANGUAGE SELECTION CONTEXT:
- User speaks: ${userLangName}
- Partner speaks: ${partnerLangName}
- Current translation direction: ${sourceName} → ${targetName}
- This is a conversation between a ${userLangName} speaker and a ${partnerLangName} speaker
- The translation should be natural and appropriate for this specific language pair`;
    }

    // Add specific instructions for Kyrgyz and Kazakh to prevent confusion
    let specificInstructions = '';
    if (targetLanguage === 'ky') {
      specificInstructions = `

🚨 CRITICAL FOR KYRGYZ TRANSLATION: 🚨
- This is KYRGYZ (Кыргызча), NOT Kazakh (Қазақша)
- Use ONLY Kyrgyz-specific vocabulary and grammar patterns
- Common Kyrgyz words: "салам" (hello), "рахмат" (thank you), "жакшы" (good), "кантип" (how), "эмне" (what)
- Kyrgyz verb forms: "айтамын" (I will say), "уктаймын" (I will sleep), "барам" (I go), "келем" (I come)
- Kyrgyz uses "ы" and "ү" sounds more frequently than Kazakh
- Kyrgyz is spoken in Kyrgyzstan, NOT Kazakhstan
- Kyrgyz has different vowel harmony than Kazakh
- FORBIDDEN: Do NOT use any Kazakh words, patterns, or vocabulary
- FORBIDDEN: Do NOT add English words to Kyrgyz translations
- If the source text appears to be Kazakh, translate it as if it were Kyrgyz
- Always use Kyrgyz spelling conventions, not Kazakh ones
- Pay attention to Kyrgyz verb conjugations and tenses

🎨 KYRGYZ AESTHETIC VOCABULARY:
- "Beautiful" = "кооз" (aesthetic beauty) or "сулуу" (handsome/pretty)
- "Pretty" = "чырагы" (radiant/beautiful)
- "Good-looking" = "жакшы көрүнүштүү"
- "Nice" = "жакшы" or "кооз"
- "Great" = "улуу" or "чоң"
- "Excellent" = "өтө жакшы" or "өтө кооз"
- "Wonderful" = "керемет" or "ажайып"
- NEVER use "супер" (super) for aesthetic descriptions - this is incorrect
- Use proper Kyrgyz words that convey the specific meaning and context`;
    } else if (targetLanguage === 'kk') {
      specificInstructions = `

🚨 CRITICAL FOR KAZAKH TRANSLATION: 🚨
- This is KAZAKH (Қазақша), NOT Kyrgyz (Кыргызча)
- Use Kazakh-specific vocabulary and grammar patterns
- Common Kazakh words: "сәлем" (hello), "рахмет" (thank you), "жақсы" (good), "қалай" (how), "не" (what)
- Kazakh uses "қ" and "ғ" sounds more frequently than Kyrgyz
- Kazakh is spoken in Kazakhstan, NOT Kyrgyzstan
- DO NOT use Kyrgyz words or patterns
- Pay attention to Kazakh verb conjugations and tenses`;
    }

    // Add context-specific instructions based on language pair
    let pairSpecificInstructions = '';
    if (userLanguage && partnerLanguage) {
      if (
        (userLanguage === 'en' && partnerLanguage === 'ky') ||
        (userLanguage === 'ky' && partnerLanguage === 'en')
      ) {
        pairSpecificInstructions = `

💬 ENGLISH-KYRGYZ CONVERSATION CONTEXT:
- This is a conversation between English and Kyrgyz speakers
- Use conversational, friendly tone appropriate for real-time communication
- Consider cultural context: English speaker may be unfamiliar with Kyrgyz culture
- Kyrgyz speaker may be unfamiliar with English cultural references
- Use clear, simple language that's easy to understand in real-time`;
      } else if (
        (userLanguage === 'en' && partnerLanguage === 'kk') ||
        (userLanguage === 'kk' && partnerLanguage === 'en')
      ) {
        pairSpecificInstructions = `

💬 ENGLISH-KAZAKH CONVERSATION CONTEXT:
- This is a conversation between English and Kazakh speakers
- Use conversational, friendly tone appropriate for real-time communication
- Consider cultural context: English speaker may be unfamiliar with Kazakh culture
- Kazakh speaker may be unfamiliar with English cultural references
- Use clear, simple language that's easy to understand in real-time`;
      } else if (
        (userLanguage === 'en' && partnerLanguage === 'ko') ||
        (userLanguage === 'ko' && partnerLanguage === 'en')
      ) {
        pairSpecificInstructions = `

💬 ENGLISH-KOREAN CONVERSATION CONTEXT:
- This is a conversation between English and Korean speakers
- Use conversational, friendly tone appropriate for real-time communication
- Consider cultural context: English speaker may be unfamiliar with Korean culture
- Korean speaker may be unfamiliar with English cultural references
- Use clear, simple language that's easy to understand in real-time`;
      }

      // Add Korean → all languages specific instructions for subject pronoun handling
      // This applies especially in lecture contexts (when conversation topic is provided)
      if (
        sourceLanguage === 'ko' &&
        conversationTopic &&
        conversationTopic.trim()
      ) {
        specificInstructions += `

🚨🚨🚨 CRITICAL: KOREAN → ${targetName.toUpperCase()} SUBJECT PRONOUN RULE (LECTURE CONTEXT - MANDATORY) 🚨🚨🚨
- This is a LECTURE/CONTEXTUAL conversation (topic provided: "${conversationTopic.trim()}")
- Korean is a pro-drop language where subjects are OFTEN omitted
- In lecture contexts, when the subject is omitted in Korean, you MUST use third person (they/они/ellos/etc.) in ${targetName}
- NEVER use first person (I/я/yo/etc.) unless the Korean text EXPLICITLY contains first person markers like "나는", "저는", "내가", "제가"
- This is a CRITICAL rule for lecture/educational contexts - you MUST follow it for accurate translation

EXAMPLES (you MUST follow these patterns):
- "배고파" → MUST translate as third person: "They're hungry" / "Они голодны" / "Ellos tienen hambre" / etc. (NOT "I'm hungry")
- "좋아해" → MUST translate as third person: "They like it" / "Им нравится" / "A ellos les gusta" / etc. (NOT "I like it")
- "가고 있어" → MUST translate as third person: "They're going" / "Они идут" / "Ellos van" / etc. (NOT "I'm going")
- "먹고 있어" → MUST translate as third person: "They're eating" / "Они едят" / "Ellos comen" / etc. (NOT "I'm eating")
- "하고 있어" → MUST translate as third person: "They're doing it" / "Они делают" / "Ellos lo hacen" / etc. (NOT "I'm doing it")

ONLY use first person if Korean explicitly says:
- "나는 배고파" → "I'm hungry" (because "나는" = "I" is explicitly stated)
- "저는 좋아해" → "I like it" (because "저는" = "I" is explicitly stated)

REMEMBER: In lecture contexts, omitted subject in Korean = ALWAYS third person in translation`;
      } else if (
        (userLanguage === 'en' &&
          (partnerLanguage === 'zh-CN' ||
            partnerLanguage === 'zh-HK' ||
            partnerLanguage === 'zh')) ||
        ((userLanguage === 'zh-CN' ||
          userLanguage === 'zh-HK' ||
          userLanguage === 'zh') &&
          partnerLanguage === 'en')
      ) {
        const chineseLang =
          userLanguage === 'zh-CN' ||
          userLanguage === 'zh-HK' ||
          userLanguage === 'zh'
            ? userLanguage
            : partnerLanguage;
        const isMandarin = chineseLang === 'zh-CN' || chineseLang === 'zh';
        const chineseType = isMandarin ? 'Mandarin' : 'Cantonese';
        pairSpecificInstructions = `

💬 ENGLISH-${chineseType.toUpperCase()} CONVERSATION CONTEXT:
- This is a conversation between English and ${chineseType} speakers
- Use conversational, friendly tone appropriate for real-time communication
- Consider cultural context: English speaker may be unfamiliar with ${chineseType} culture
- ${chineseType} speaker may be unfamiliar with English cultural references
- Use clear, simple language that's easy to understand in real-time`;
      } else if (
        (userLanguage === 'en' && partnerLanguage === 'ru') ||
        (userLanguage === 'ru' && partnerLanguage === 'en')
      ) {
        pairSpecificInstructions = `

💬 ENGLISH-RUSSIAN CONVERSATION CONTEXT:
- This is a conversation between English and Russian speakers
- Use conversational, friendly tone appropriate for real-time communication
- Consider cultural context: English speaker may be unfamiliar with Russian culture
- Russian speaker may be unfamiliar with English cultural references
- Use clear, simple language that's easy to understand in real-time`;
      } else if (
        (userLanguage === 'ko' && partnerLanguage === 'ky') ||
        (userLanguage === 'ky' && partnerLanguage === 'ko')
      ) {
        pairSpecificInstructions = `

💬 KOREAN-KYRGYZ CONVERSATION CONTEXT:
- This is a conversation between Korean and Kyrgyz speakers
- Use conversational, friendly tone appropriate for real-time communication
- Consider cultural context: Korean speaker may be unfamiliar with Kyrgyz culture
- Kyrgyz speaker may be unfamiliar with Korean cultural references
- Use clear, simple language that's easy to understand in real-time
- Pay special attention to Korean honorifics and Kyrgyz politeness forms
- Korean uses formal/informal speech levels - translate appropriately
- Kyrgyz has different politeness markers - adapt Korean formality to Kyrgyz context

🚨 CRITICAL TRANSLATION ACCURACY:
- Translate the EXACT meaning, not a different concept
- "I'm hungry" (배고파/мен ачка) should translate to "I'm hungry" in the target language
- "I like you" (좋아해/жакшы көрөм) should translate to "I like you" in the target language
- NEVER change the core meaning or sentiment of the message
- If the source text is about hunger, the translation must also be about hunger
- If the source text is about liking someone, the translation must also be about liking someone
- Maintain semantic consistency across both translation directions`;
      } else if (
        (userLanguage === 'ko' && partnerLanguage === 'kk') ||
        (userLanguage === 'kk' && partnerLanguage === 'ko')
      ) {
        pairSpecificInstructions = `

💬 KOREAN-KAZAKH CONVERSATION CONTEXT:
- This is a conversation between Korean and Kazakh speakers
- Use conversational, friendly tone appropriate for real-time communication
- Consider cultural context: Korean speaker may be unfamiliar with Kazakh culture
- Kazakh speaker may be unfamiliar with Korean cultural references
- Use clear, simple language that's easy to understand in real-time
- Pay special attention to Korean honorifics and Kazakh politeness forms
- Korean uses formal/informal speech levels - translate appropriately
- Kazakh has different politeness markers - adapt Korean formality to Kazakh context`;
      } else if (
        ((userLanguage === 'zh-CN' ||
          userLanguage === 'zh-HK' ||
          userLanguage === 'zh') &&
          partnerLanguage === 'ky') ||
        (userLanguage === 'ky' &&
          (partnerLanguage === 'zh-CN' ||
            partnerLanguage === 'zh-HK' ||
            partnerLanguage === 'zh'))
      ) {
        pairSpecificInstructions = `

💬 CHINESE-KYRGYZ CONVERSATION CONTEXT:
- This is a conversation between Chinese and Kyrgyz speakers
- Use conversational, friendly tone appropriate for real-time communication
- Consider cultural context: Chinese speaker may be unfamiliar with Kyrgyz culture
- Kyrgyz speaker may be unfamiliar with Chinese cultural references
- Use clear, simple language that's easy to understand in real-time`;
      } else if (
        ((userLanguage === 'zh-CN' ||
          userLanguage === 'zh-HK' ||
          userLanguage === 'zh') &&
          partnerLanguage === 'kk') ||
        (userLanguage === 'kk' &&
          (partnerLanguage === 'zh-CN' ||
            partnerLanguage === 'zh-HK' ||
            partnerLanguage === 'zh'))
      ) {
        pairSpecificInstructions = `

💬 CHINESE-KAZAKH CONVERSATION CONTEXT:
- This is a conversation between Chinese and Kazakh speakers
- Use conversational, friendly tone appropriate for real-time communication
- Consider cultural context: Chinese speaker may be unfamiliar with Kazakh culture
- Kazakh speaker may be unfamiliar with Chinese cultural references
- Use clear, simple language that's easy to understand in real-time`;
      } else if (
        (userLanguage === 'ru' && partnerLanguage === 'ky') ||
        (userLanguage === 'ky' && partnerLanguage === 'ru')
      ) {
        pairSpecificInstructions = `

💬 RUSSIAN-KYRGYZ CONVERSATION CONTEXT:
- This is a conversation between Russian and Kyrgyz speakers
- Use conversational, friendly tone appropriate for real-time communication
- Consider cultural context: Russian speaker may be unfamiliar with Kyrgyz culture
- Kyrgyz speaker may be unfamiliar with Russian cultural references
- Use clear, simple language that's easy to understand in real-time`;
      } else if (
        (userLanguage === 'ru' && partnerLanguage === 'kk') ||
        (userLanguage === 'kk' && partnerLanguage === 'ru')
      ) {
        pairSpecificInstructions = `

💬 RUSSIAN-KAZAKH CONVERSATION CONTEXT:
- This is a conversation between Russian and Kazakh speakers
- Use conversational, friendly tone appropriate for real-time communication
- Consider cultural context: Russian speaker may be unfamiliar with Kazakh culture
- Kazakh speaker may be unfamiliar with Russian cultural references
- Use clear, simple language that's easy to understand in real-time`;
      }
    }

    // Add conversation topic context if provided
    let topicContext = '';
    if (conversationTopic && conversationTopic.trim()) {
      const topic = conversationTopic.trim();
      topicContext = `\n\nCONVERSATION CONTEXT:\nThis conversation is about: "${topic}". The topic may be in any language - understand it regardless of the language used.\n\n🚨 CRITICAL RULES:\n- Use domain-specific terminology and context to provide more accurate translations\n- Translate ONLY what the user ACTUALLY SPOKE - DO NOT create stories or add information\n- DO NOT expand on the topic or generate examples/explanations\n- When translating, prioritize terms and phrases relevant to this topic, but translate ONLY the actual speech\n- The topic is for understanding context ONLY - NOT for generating new content`;
    }

    return `You are a professional translator specializing in ${targetName}. Translate from ${sourceName} to ${targetName}.${languageContext}${specificInstructions}${pairSpecificInstructions}${topicContext}

🚨 CRITICAL TRANSLATION RULES:
- Translate the EXACT meaning and sentiment, not a different concept
- Maintain semantic consistency - if source is about hunger, translation must be about hunger
- NEVER change the core meaning or intent of the message
- NEVER mix languages - use ONLY the target language vocabulary
- NEVER add English words to non-English translations
- NEVER add words from other languages to the translation
- NEVER generate random or unrelated content
- NEVER add phrases like "thank you for watching" or similar
- NEVER add extra words that weren't in the original
${
  sourceLanguage === 'ko' && conversationTopic && conversationTopic.trim()
    ? '- 🚨 KOREAN LECTURE CONTEXT: When Korean subject is omitted AND conversation topic is provided, you MUST use third person (they/они/ellos) NOT first person (I/я/yo). This is MANDATORY.'
    : ''
}
- Use natural conversation style appropriate for real-time communication
- Return only the translation without explanations
- Use proper native vocabulary that conveys the exact meaning
- If the source text is unclear, make your best attempt based on context
- If the source text is nonsensical, return a simple acknowledgment in the target language

🚨 QUALITY CONTROL:
- Double-check that your translation makes sense in the target language
- Ensure the translation is appropriate for the conversation context
- Verify that you're using the correct target language (${targetName})
- Confirm that the translation conveys the same meaning as the source

Rules: Only translate to ${targetName}. Use natural conversation style. Return only the translation.`;
  }

  /**
   * Validate and filter translation results to ensure quality
   */
  private validateAndFilterTranslation(
    translation: string,
    originalText: string,
    targetLanguage: string
  ): string {
    console.log('🔍 VALIDATION DEBUG - Input:', {
      translation,
      originalText,
      targetLanguage,
    });

    if (!translation || translation.trim() === '') {
      console.log('⚠️ Empty translation received, returning original text');
      return originalText;
    }

    const trimmedTranslation = translation.trim();
    console.log(
      '🔍 VALIDATION DEBUG - Trimmed translation:',
      trimmedTranslation
    );

    // Filter out common unwanted phrases
    const unwantedPhrases = [
      'thank you for watching',
      'thanks for watching',
      'subscribe to my channel',
      'like and subscribe',
      'please subscribe',
      "don't forget to subscribe",
      'hit the like button',
      'comment below',
      'share this video',
      'follow me',
      'check out my channel',
      'visit my website',
      'link in description',
      'click the link',
      'see you next time',
      'until next time',
      'goodbye everyone',
      'take care everyone',
      'bye bye',
      'see you later',
      'talk to you later',
    ];

    const lowerTranslation = trimmedTranslation.toLowerCase();

    for (const phrase of unwantedPhrases) {
      if (lowerTranslation.includes(phrase)) {
        console.log(
          '🚫 Filtered out unwanted phrase from translation:',
          phrase
        );
        console.log('📝 Original text:', originalText);
        console.log('📝 Filtered translation:', trimmedTranslation);

        // Return a simple acknowledgment instead
        if (targetLanguage === 'ky') {
          return 'Түшүндүм';
        } else if (targetLanguage === 'kk') {
          return 'Түсіндім';
        } else if (targetLanguage === 'ko') {
          return '알겠습니다';
        } else if (targetLanguage === 'zh') {
          return '知道了';
        } else if (targetLanguage === 'ru') {
          return 'Понял';
        } else {
          return 'Understood';
        }
      }
    }

    // Check for suspicious patterns that might indicate poor translation
    const suspiciousPatterns = [
      /^[a-zA-Z\s]+$/, // All English letters when target is not English
      /^\d+$/, // Just numbers
      // Removed /^[^\w\s]+$/ pattern as it incorrectly flags Cyrillic characters as symbols
      // Removed very short pattern as it incorrectly flags valid short words like "ат" (horse)
    ];

    console.log(
      '🔍 VALIDATION DEBUG - Checking suspicious patterns for:',
      trimmedTranslation
    );
    console.log('🔍 VALIDATION DEBUG - Target language:', targetLanguage);

    // Only apply these checks for non-English targets
    if (targetLanguage !== 'en') {
      for (let i = 0; i < suspiciousPatterns.length; i++) {
        const pattern = suspiciousPatterns[i];
        const matches = pattern.test(trimmedTranslation);
        console.log(
          `🔍 VALIDATION DEBUG - Pattern ${i} (${pattern}): ${matches}`
        );

        if (matches) {
          console.log(
            '⚠️ Suspicious translation pattern detected:',
            trimmedTranslation
          );
          console.log('📝 Original text:', originalText);
          console.log('🚫 Returning fallback instead of original translation');

          // Return a simple acknowledgment instead
          if (targetLanguage === 'ky') {
            return 'Түшүндүм';
          } else if (targetLanguage === 'kk') {
            return 'Түсіндім';
          } else if (targetLanguage === 'ko') {
            return '알겠습니다';
          } else if (targetLanguage === 'zh') {
            return '知道了';
          } else if (targetLanguage === 'ru') {
            return 'Понял';
          }
        }
      }
    }

    // Check for extremely long translations that might be errors
    if (trimmedTranslation.length > originalText.length * 3) {
      console.log('⚠️ Translation seems too long, might be an error');
      console.log('📝 Text lengths: [processed]');
      console.log('📝 Original text:', originalText);
      console.log('📝 Translation:', trimmedTranslation);
    }

    console.log(
      '✅ Translation passed validation, returning:',
      trimmedTranslation
    );
    return trimmedTranslation;
  }

  // Handle authentication errors with toast notification and automatic logout
  private async handleAuthError(error: Error): Promise<void> {
    console.error('🔐 Authentication error:', error);
    console.log(
      '🚨 handleAuthError called - starting automatic logout process'
    );

    // First, immediately start the logout process
    console.log('🚪 Starting immediate logout...');
    this.authService.logout();
    console.log('✅ User logged out due to authentication error');

    // Then try to show notification (non-blocking)
    console.log('🍞 Attempting to show toast notification (non-blocking)...');
    this.showNotificationAsync(
      'Your session has expired. Please log in again.'
    );

    // Also try a simple browser alert as backup
    setTimeout(() => {
      console.log('🔔 Showing browser alert as backup notification...');
      alert('Your session has expired. Please log in again.');
    }, 500);

    // Force change detection to ensure UI updates
    setTimeout(() => {
      console.log('🔄 Forcing change detection after logout');
      // Trigger a small change to force Angular change detection
      this.authService.currentUser$.subscribe((user) => {
        console.log('🔍 Current user after logout:', user);
      });

      // Additional force logout attempt
      console.log('🔄 Additional logout attempt...');
      this.authService.logout();
    }, 100);
  }

  // Non-blocking notification method
  private async showNotificationAsync(message: string): Promise<void> {
    try {
      console.log('🍞 Attempting toast notification...');
      await this.toastService.showError(message, 5000);
      console.log('✅ Toast notification shown successfully');
    } catch (toastError) {
      console.error('❌ Toast error:', toastError);
      console.log('⚠️ Using browser alert as fallback...');
      // Use browser alert as fallback
      alert(message);
    }
  }
}
